import { FileX, PauseCircle, Wallet, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { isNotApplicableReason, isRecoverableHaltReason } from '@aica/core';

import { isHaltedRecordStatus } from '@/lib/records/status';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DEMO_RECORD_NEXT } from '@/features/demo/demo-record-next';

/**
 * The `not_applicable` reason this notice knows how to explain, or `null`
 * when there is none — `errorMessage` absent, or a cause outside the shared
 * list. `null` is not "nothing renders": the caller falls back to the neutral
 * status-level copy.
 *
 * The list itself lives in `@aica/core` (`NOT_APPLICABLE_REASONS`) rather than
 * here. It used to be a local set of the three reasons the hybrid-media
 * resolver wrote, while the two commonest closes — a missed call and "no
 * recording file in the CRM activity" — wrote no reason at all. Neither half
 * could see the other, so the gap stayed invisible: the pipeline closed the
 * record, the notice fell through to the generic text, and the customer read
 * that a recording had been received when none existed.
 *
 * The same shape of gap survived the shared list once already: the chat steps
 * kept writing English prose into the same column, which `isNotApplicableReason`
 * rejected, so those closes fell through to the generic text exactly as before.
 * A reason the pipeline writes belongs in the registry, or this component is
 * blind to it by construction.
 */
function knownNotApplicableReason(
  status: string | null | undefined,
  errorMessage: string | null | undefined
): string | null {
  return status === 'not_applicable' && isNotApplicableReason(errorMessage)
    ? errorMessage
    : null;
}

/**
 * Четыре грани одного случая — глиф, тон, три запасные строки — держатся в
 * ОДНОЙ записи.
 *
 * Раньше каждая грань выводилась своей цепочкой тернарников по тем же
 * признакам. Четыре цепочки типизируются независимо, поэтому пятый случай или
 * переставленная ветка в одной из них давали уведомление, где иконка говорит
 * одно, а текст другое, — без ошибки сборки и без падения теста.
 *
 * `tone` отделяет ожидание действия от закрытой записи. Жёлтый обещает, что
 * что-то можно сделать: пополнить кошелёк, включить анализ. У `not_applicable`
 * делать нечего, и бейдж того же статуса рисуется нейтральным
 * (`status-badge.tsx`) — карточка обязана говорить то же самое.
 */
type HaltedKind = 'reason' | 'funds' | 'closed' | 'not_started' | 'not_started_again';

/**
 * Запасные строки — то, что i18next печатает, когда ключа в локали не нашлось.
 *
 * Экспортируются ради собственного теста. Достать их через рендер нельзя: пока
 * бандлы полны, до них не доходит ни один путь, — а неверное утверждение в
 * запасной строке ждёт ровно того дня, когда бандл окажется неполон.
 */
export const HALTED_COPY: Record<
  HaltedKind,
  {
    icon: typeof FileX;
    tone: 'awaiting' | 'closed';
    title: string;
    body: string;
    next: string;
  }
> = {
  reason: {
    icon: FileX,
    tone: 'closed',
    // Запасной текст для ЛЮБОЙ причины из реестра, поэтому он не вправе
    // утверждать ничего сверх «причина названа». Прежний говорил «звонок
    // принят, но записи к нему не приложилось» — про пропущенный звонок это
    // ложь дважды (никто не ответил), про брошенную переписку — ещё и не про
    // звонок. Сработает он ровно тогда, когда в локали не нашлось ключа
    // причины, то есть в единственном месте, где компонент и должен быть
    // подстраховкой, а не источником нового неверного утверждения.
    title: 'This record was closed for a named reason',
    body: 'The pipeline recorded why this conversation will not be transcribed or analyzed.',
    next: 'Nothing to do automatically.',
  },
  funds: {
    icon: Wallet,
    tone: 'awaiting',
    title: 'Processing stopped — account out of funds',
    body: 'The call was received and stored, but transcription and analysis never ran: the organization has no funds left.',
    next: 'Top up the balance, then start the collected records — they do not resume on their own.',
  },
  closed: {
    icon: XCircle,
    tone: 'closed',
    title: 'Nothing to analyze',
    // «Conversation», а не «record»: этот текст — запасной для ВСЕХ причин
    // `not_applicable`, включая брошенный чат, поэтому «звонок» здесь сказать
    // нельзя. Но и «запись» нельзя: по-русски это слово означает и строку в
    // системе, и аудио, — и клиент читал «аудиозапись получена и лежит» ровно
    // там, где её нет. Соседние статусы говорят «звонок принят», и параллель
    // формулировок важнее краткости.
    body: 'The conversation was received and stored, but there is nothing here for transcription or analysis to work with.',
    next: 'No action needed — this record will not be processed further.',
  },
  not_started: {
    icon: PauseCircle,
    tone: 'awaiting',
    // Раньше звало «включить анализ в списке записей» — совет из режима, где
    // автоанализ включался целиком. В ручном режиме нужное действие — кнопка
    // тут же, на этом уведомлении (см. `action` ниже), и текст обязан вести к
    // ней, а не в другое место интерфейса.
    title: 'Not analyzed yet',
    // Не «расшифровка и анализ не запускались»: в этот статус попадает и запись,
    // которую повтор оставил ждать запуска после сбоя (#1264), — у неё
    // расшифровка запускалась и упала.
    body: 'The call was received and stored. Analysis is not running, and no minutes are spent until it is started.',
    next: 'Start the analysis for this record — or switch to automatic mode and new conversations will be analyzed on their own.',
  },
  // Запись, которую повтор оставил ждать запуска (#1264): прогон у неё уже был,
  // и транскрипт или анализ от него лежат рядом. «Расшифровка и анализ не
  // запускались» здесь было бы неправдой. Совет про автоматический режим тоже
  // не годится: причиной мог быть выключенный автоанализ проекта.
  not_started_again: {
    icon: PauseCircle,
    tone: 'awaiting',
    title: 'Waiting to be started again',
    body: 'This record has already been processed once, and what that run produced is still shown. Nothing runs and no minutes are spent until it is started.',
    next: 'Start the analysis for this record.',
  },
};

interface RecordHaltedNoticeProps {
  /** `record.status`. Anything outside the halted set renders nothing. */
  status?: string | null;
  /**
   * `record.errorMessage`. Only consulted for `not_applicable`, and only to
   * pick a MORE SPECIFIC copy when it names one of `NOT_APPLICABLE_REASONS` —
   * absent or unrecognized falls back to the neutral `not_applicable` text,
   * not to blank space.
   */
  errorMessage?: string | null;
  className?: string;
  /**
   * Право запускать анализ — решает сервер, компонент лишь отражает его
   * ответ. `undefined`/`false` ведут себя одинаково: кнопки нет. Отдельный
   * пропс, а не вывод из наличия `onStart`, потому что запись остаётся
   * читаемой (агент, супервизор) даже без права её запустить — здесь важно
   * не подставить кнопку, которая ответит 403.
   */
  canStart?: boolean;
  /**
   * Запускает анализ ЭТОЙ записи — со стороны вызывающего через
   * `useQuotedStart`: сначала цена, запуск только после подтверждения
   * (#1389). Кнопка рендерится только при `canStart && onStart`
   * — оба условия нужны: право есть, но без обработчика нажимать нечего.
   */
  onStart?: () => void;
  /** Идёт запрос цены или мутация запуска — кнопка блокируется, чтобы не
   * задвоить запрос. */
  isStarting?: boolean;
  /**
   * Вернуть в обработку запись, закрытую по ВОССТАНОВИМОЙ причине
   * (`RECOVERABLE_HALT_REASONS` — сегодня это только «источник отключён»).
   * Отдельный обработчик, а не `onStart`: там запускается
   * анализ уже собранного разговора, здесь — повторная попытка забрать медиа,
   * это разный эндпоинт (#460).
   */
  onResume?: () => void;
  /** Идёт возврат в обработку — та же защита от задвоения. */
  isResuming?: boolean;
  /**
   * `record.type`. Нужен ровно для одного: `source_disconnected` пишет не
   * только медиа-ветка — доставка, у которой отключились все адресаты,
   * закрывает так же, и такая запись может быть чатом. Возврат для чата сервер
   * отвергает (`awaitMedia` применим только к звонкам), поэтому кнопка там
   * была бы мёртвой (ревью Codex на #567).
   */
  recordType?: string | null;
  /**
   * У записи уже есть транскрипт или анализ от прошлого прогона. Для
   * `awaiting_start` это запись, которую повтор оставил ждать запуска, и текст
   * «ничего не запускалось» про неё неверен (#1264).
   */
  hasEarlierResults?: boolean;
  /**
   * Демо-запись (#1503): её не запускают и не возвращают в обработку, поэтому
   * вместо шага «запустите» — объяснение, и кнопок нет.
   */
  isDemo?: boolean;
}

/**
 * Why an opened record shows neither transcript nor analysis, when the reason
 * is a decision rather than a delay.
 *
 * All three panels used to fall back to "not available yet — processing may
 * still be in progress". That is false for every status this component
 * covers: `awaiting_funds`/`awaiting_start` are waiting on a person, and
 * `not_applicable` is a closed decision the pipeline will never revisit —
 * missed call, abandoned chat, recording confirmed absent at its source, or
 * (a later addition) audio expected from a connected telephony install that
 * never arrived. Each state therefore says three things: what happened, why,
 * and the one move that changes it — or, for `not_applicable` without a more
 * specific cause, that there is no move because the record is genuinely done.
 *
 * `not_applicable`'s STATUS-LEVEL copy has to stay true across all of its
 * causes at once, so it stays deliberately generic ("nothing to analyze").
 * Every reason in `NOT_APPLICABLE_REASONS` gets its own, more specific copy
 * keyed by `errorMessage` — the two commonest call closes, the three
 * hybrid-media-source outcomes, a disconnected source, and the three chat
 * closes. The count is deliberately NOT restated here: it went stale within
 * one commit last time, and `NOT_APPLICABLE_REASONS` is the list that
 * enumerates itself.
 */
export function RecordHaltedNotice({
  status,
  errorMessage,
  className,
  canStart,
  onStart,
  isStarting,
  onResume,
  isResuming,
  recordType,
  hasEarlierResults,
  isDemo = false,
}: RecordHaltedNoticeProps) {
  const { t } = useTranslation('records');

  if (!isHaltedRecordStatus(status)) return null;

  const reason = knownNotApplicableReason(status, errorMessage);
  const kind: HaltedKind = reason
    ? 'reason'
    : status === 'awaiting_funds'
      ? 'funds'
      : status === 'not_applicable'
        ? 'closed'
        : hasEarlierResults && status === 'awaiting_start'
          ? 'not_started_again'
          : 'not_started';
  const { icon: Icon, tone, title, body, next } = HALTED_COPY[kind];
  const key = reason
    ? `records.halted.${status}.${reason}`
    : kind === 'not_started_again'
      ? 'records.halted.awaiting_start.again'
      : `records.halted.${status}`;
  const isAwaiting = tone === 'awaiting';
  // Только `awaiting_start`/`awaiting_funds` (kind `not_started`/`funds`) — там
  // есть что запускать. `not_applicable` — решение, которое конвейер не
  // пересмотрит, и кнопка там была бы обещанием несуществующего действия.
  // `canStart` — ответ сервера, а не догадка компонента; без `onStart` нажимать
  // тоже нечего.
  const showStartButton = isAwaiting && !isDemo && Boolean(canStart) && Boolean(onStart);

  // Исключение из «`not_applicable` — решение, которое конвейер не
  // пересмотрит»: одна причина реестра держится на обстоятельстве СНАРУЖИ, и
  // обстоятельство меняется. Источник отключили, потом переподключили —
  // разговор пригоден, помешала интеграция. Остальные кнопки не получают:
  // они невосстановимы по природе, а `chat_timeout` восстановим по
  // обстоятельству, но снять его нечем — разбор в `RECOVERABLE_HALT_REASONS`
  // (#460).
  //
  // `canStart` как право — сознательно УЖЕ, чем требует сервер: там нужен
  // `record.update` (в том числе supervisor/agent по проекту), а `canStart`
  // отвечает `organization.update`, то есть client/admin. Ошибка в
  // безопасную сторону: кто-то не увидит кнопку, но никто не увидит кнопку,
  // которая ответит 403.
  const showResumeButton =
    isRecoverableHaltReason(reason) &&
    !isDemo &&
    (recordType ?? 'call') === 'call' &&
    Boolean(canStart) &&
    Boolean(onResume);

  return (
    <div
      data-testid="record-halted-notice"
      className={cn(
        'rounded-card border p-4',
        isAwaiting ? 'border-warning/30 bg-warning-dim' : 'border-border bg-bg-surface',
        className
      )}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            'h-5 w-5 flex-shrink-0',
            isAwaiting ? 'text-warning' : 'text-fg-muted'
          )}
          aria-hidden="true"
        />
        <div className="space-y-1.5">
          <p className="text-sm font-semibold text-fg">{t(`${key}.title`, title)}</p>
          <p className="text-xs leading-relaxed text-fg-secondary">
            {t(`${key}.body`, body)}
          </p>
          <p className="text-xs leading-relaxed text-fg-muted">
            {isDemo && (isAwaiting || isRecoverableHaltReason(reason))
              ? t('records.demoRecordNext', DEMO_RECORD_NEXT)
              : t(`${key}.next`, next)}
          </p>
          {showStartButton && (
            <Button size="sm" onClick={onStart} isLoading={isStarting} className="mt-1">
              {t(`${key}.action`, 'Analyze')}
            </Button>
          )}
          {showResumeButton && (
            <Button size="sm" onClick={onResume} isLoading={isResuming} className="mt-1">
              {t(`${key}.action`, 'Try again')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
